import { Temporal } from 'temporal-polyfill';
import type {
  ApplyRun,
  EnvDetailV0,
  EnvDetailV0Wire,
  EnvOverviewRow,
  EnvOverviewRowWire,
  EnvOverviewV0,
  EnvOverviewV0Wire,
  SourceFreshness,
  TerragruntUnit,
  TerragruntUnitWire,
} from '@internal/plugin-environments-common';

type PowerEventWire = EnvOverviewRowWire['power']['lastShutdown'];
type SourceFreshnessWire = EnvOverviewV0Wire['sources'][number];
type ApplyRunWire = TerragruntUnitWire['history'][number];

const instant = (iso: string) => Temporal.Instant.from(iso);
const optional = (iso?: string) => (iso ? instant(iso) : undefined);
const event = (e: PowerEventWire) => e && { ...e, at: instant(e.at) };

function parseOverviewRow(r: EnvOverviewRowWire): EnvOverviewRow {
  const { power, deploy, infra } = r;
  return {
    ...r,
    power: {
      ...power,
      lastShutdown: event(power.lastShutdown),
      lastStartup: event(power.lastStartup),
      keepalive: power.keepalive && {
        ...power.keepalive,
        deadline: instant(power.keepalive.deadline),
      },
    },
    deploy: {
      ...deploy,
      finishedAt: optional(deploy.finishedAt),
      recent: deploy.recent?.map(d => ({ ...d, at: instant(d.at) })),
      recentSince: optional(deploy.recentSince),
    },
    infra: infra && {
      ...infra,
      lastAppliedAt: optional(infra.lastAppliedAt),
      recentApplies: infra.recentApplies?.map(instant),
      recentSince: optional(infra.recentSince),
    },
  };
}

const parseSource = (s: SourceFreshnessWire): SourceFreshness => ({
  ...s,
  fetchedAt: optional(s.fetchedAt),
  dataAsOf: optional(s.dataAsOf),
});

export const parseOverview = (o: EnvOverviewV0Wire): EnvOverviewV0 => ({
  ...o,
  generatedAt: instant(o.generatedAt),
  rows: o.rows.map(parseOverviewRow),
  sources: o.sources.map(parseSource),
});

const duration = (iso?: string) =>
  iso ? Temporal.Duration.from(iso) : undefined;

const applyRun = (a: ApplyRunWire): ApplyRun => ({
  ...a,
  at: instant(a.at),
  duration: duration(a.duration),
});

const parseUnit = (u: TerragruntUnitWire): TerragruntUnit => ({
  ...u,
  lastApply: u.lastApply && applyRun(u.lastApply),
  cleanPlan: u.cleanPlan && { ...u.cleanPlan, at: instant(u.cleanPlan.at) },
  history: u.history.map(applyRun),
  stateWrittenAt: optional(u.stateWrittenAt),
});

export const parseEnvDetail = (d: EnvDetailV0Wire): EnvDetailV0 => ({
  ...d,
  generatedAt: instant(d.generatedAt),
  row: parseOverviewRow(d.row),
  deployHistory: d.deployHistory.map(h => ({
    ...h,
    at: instant(h.at),
    duration: duration(h.duration),
  })),
  units: d.units.map(parseUnit),
  appliesSince: optional(d.appliesSince),
  sources: d.sources.map(parseSource),
});
